import { useEffect, useRef, useState } from 'react';

// jsdom and older browsers don't implement matchMedia.
const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// Gentle start and a visible slow-down at the end; ease-out alone rushes the start and seems to stall.
const easeInOutCubic = (progress: number) => (progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2);

const useAnimatedNumber = (target: number, duration = 500) => {
  const [value, setValue] = useState(0);
  const valueRef = useRef(0);
  const isReducedMotion = prefersReducedMotion();

  useEffect(() => {
    if (isReducedMotion) return;

    const from = valueRef.current;
    let startedAt: number | undefined;
    let frame = 0;

    const step = (now: number) => {
      startedAt ??= now;
      const progress = Math.min((now - startedAt) / duration, 1);
      valueRef.current = from + (target - from) * easeInOutCubic(progress);
      setValue(valueRef.current);
      if (progress < 1) frame = requestAnimationFrame(step);
    };

    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [target, duration, isReducedMotion]);

  return isReducedMotion ? target : value;
};

export default useAnimatedNumber;
